import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, output, ViewChild } from '@angular/core';
import { AuthStateAdaptorService, UserType } from '@aiwyn/auth';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, startWith, take, takeUntil, tap } from 'rxjs/operators';
import { AbstractControl, FormControlStatus, UntypedFormArray, UntypedFormControl, Validators } from '@angular/forms';
import { combineLatest, defer, Observable, of, Subject } from 'rxjs';
import { getControlValueChanges, removeDuplicates, removeSpecifiedValues, Validate } from '@aiwyn/shared';

export type RecipientEmailsDisabledState = boolean | 'primary' | 'additional';

// Debounce for live (per-keystroke) primary-email validation, so the error/gate doesn't flicker mid-word.
const PRIMARY_EMAIL_LIVE_VALIDATION_DEBOUNCE_TIME = 300;

@Component({
  selector: 'app-recipient-emails',
  templateUrl: './recipient-emails.component.html',
  styleUrls: ['./recipient-emails.component.scss'],
  standalone: false,
})
export class RecipientEmailsComponent implements Validate, OnInit, OnDestroy {
  isDisabled: RecipientEmailsDisabledState;
  @Input() set disabled(disabled: RecipientEmailsDisabledState) {
    this.isDisabled = disabled;
    if (!disabled) {
      // TODO: Remove AuthState dependency, should instead be passed in as an input
      if (!this.authStateAdaptor.getUserEmailSnapshot()) {
        this.primaryEmailControl.enable();
      }
      this.additionalEmailsFormArray.enable();
      return;
    }

    []
      .concat(disabled === true || disabled === 'primary' ? this.primaryEmailControl : [])
      .concat(disabled === true || disabled === 'additional' ? this.additionalEmailsFormArray : [])
      .forEach((control: AbstractControl) => control.disable());
  }

  @Input() set primaryEmail(primaryEmail: string) {
    if (primaryEmail == undefined || this.primaryEmailControl.value) {
      return;
    }

    const userType = this.authStateAdaptor.getUserTypeSnapshot();
    this.primaryEmailControl.setValue(primaryEmail, { emitEvent: userType === UserType.GUEST });
  }

  @Input() additionalEmails: string[];

  @Output() primaryEmailChanged = new EventEmitter<string>();
  @Output() additionalEmailsChanged = new EventEmitter<string[]>();
  @Output() isValid = new EventEmitter<boolean>();
  // True while the primary email is prepopulated AND the payer has not edited it. Lets a host hide the
  // email-gate status banners, which are noise for an email the payer never had to supply.
  primaryEmailPrepopulated = output<boolean>();

  private readonly _unsubscribe$ = new Subject<void>();

  additionalEmailsFormArray = new UntypedFormArray([]);
  primaryEmailControl = new UntypedFormControl('', { validators: [Validators.required, Validators.email], updateOn: 'blur' });

  // Lets a host move focus back to the email field (e.g. when a payer clicks a locked payment method
  // before entering a valid email). Decorator query (not signal viewChild) because ng-mocks cannot yet
  // mock components with signal view queries.
  @ViewChild('primaryEmailInput') private readonly primaryEmailInput?: ElementRef<HTMLInputElement>;

  /**
   * Specifically NOT waiting for loaded results since our auth process can revert state late in its process, causing already loaded data
   * to be cleared. To accommodate this, simply enable the primary email control if the user email is not present. Auth refactor looming.
   */
  togglePrimaryEmailControlEnabledState$ = this.authStateAdaptor.getUserEmail().pipe(
    tap(userEmail => {
      const toggleFn: keyof UntypedFormControl & ('disable' | 'enable') = userEmail ? 'disable' : 'enable';
      this.primaryEmailControl[toggleFn]();
    }),
    takeUntil(this._unsubscribe$),
  );

  emitPrimaryEmailChanges$ = getControlValueChanges<string>(this.primaryEmailControl).pipe(
    filter(_ => !this.primaryEmailControl.invalid),
    map(this._formatEmail),
    distinctUntilChanged(),
    tap(email => this.primaryEmailChanged.emit(email)),
    tap(email => {
      const additionalEmails = this.additionalEmailsFormArray.controls.map(({ value }) => this._formatEmail(value));
      if (additionalEmails.includes(email)) {
        this.additionalEmailsFormArray.updateValueAndValidity();
      }
    }),
    takeUntil(this._unsubscribe$),
  );

  emitAdditionalEmailsChanges$ = getControlValueChanges<string[]>(this.additionalEmailsFormArray).pipe(
    filter(_ => !this.additionalEmailsFormArray.invalid),
    map(emails => {
      const validEmails = emails.filter(Boolean).map(this._formatEmail);
      const uniqueValidEmails = removeDuplicates(validEmails);
      const uniqueValidAdditionalEmails = removeSpecifiedValues(
        uniqueValidEmails,
        this._formatEmail(this.primaryEmailControl.value),
      );

      return uniqueValidAdditionalEmails;
    }),
    distinctUntilChanged(),
    tap(emails => this.additionalEmailsChanged.emit(emails)),
    takeUntil(this._unsubscribe$),
  );

  // Shared "is the email requirement satisfied" stream (VALID or DISABLED pass, mirroring validate()).
  // Drives both (isValid) and the gate-banner relevance below off one source, so they can never disagree.
  // The PRIMARY email is the only datapoint that gates payment-method creation — the optional additional
  // recipients being invalid must never lock the methods (validate() still blocks the actual payment).
  // Deliberately no isContactOrAdmin shortcut: those users' control is disabled (which passes), and
  // hard-coding VALID would detach the gate from the control's real validity if it is ever left enabled.
  // Seeding with the current status matters: defer() reads it lazily at subscribe (ngOnInit) so a control
  // disabled/valued during construction is reflected, and statusChanges alone never emits on subscribe.
  private _primaryEmailSatisfied$ = defer(() =>
    this.primaryEmailControl.statusChanges.pipe(startWith(this.primaryEmailControl.status)),
  ).pipe(
    map(primaryEmailStatus => this._isStatusSatisfied(primaryEmailStatus)),
    distinctUntilChanged(),
    takeUntil(this._unsubscribe$),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  emitIsValid$ = this._primaryEmailSatisfied$.pipe(
    tap(isValid => this.isValid.emit(isValid)),
    takeUntil(this._unsubscribe$),
  );

  // Latches true on the payer's first real edit. A UI edit marks the control dirty; a programmatic
  // prefill fires valueChanges but never marks it dirty, so prefills are excluded.
  private _primaryEmailEditedByUser$ = this.primaryEmailControl.valueChanges.pipe(
    filter(() => this.primaryEmailControl.dirty),
    map(() => true),
    startWith(false),
    distinctUntilChanged(),
    takeUntil(this._unsubscribe$),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  // Hide the gate status banners while the email is prepopulated (satisfied on the first evaluation) AND
  // untouched. Once the payer edits the field they own it, so the banners resume following its validity.
  // A payer who starts unsatisfied is never suppressed.
  emitPrimaryEmailPrepopulated$ = combineLatest([
    this._primaryEmailSatisfied$.pipe(take(1)),
    this._primaryEmailEditedByUser$,
  ]).pipe(
    map(([prepopulatedOnLoad, editedByUser]) => prepopulatedOnLoad && !editedByUser),
    distinctUntilChanged(),
    tap(prepopulated => this.primaryEmailPrepopulated.emit(prepopulated)),
    takeUntil(this._unsubscribe$),
  );

  private _isStatusSatisfied(status: FormControlStatus): boolean {
    return status === 'VALID' || status === 'DISABLED';
  }

  // Live-validation escalation: the control starts at updateOn:'blur' (no error nagging while the payer types
  // their email the first time), but once a committed value is non-empty AND invalid we escalate to validating
  // on every (debounced) keystroke so the error and the gate recover the moment the email is fixed. updateOn
  // cannot be changed on a live control, so 'change' mode is emulated by committing view values from (input)
  // events below. The escalation is one-way: it never reverts to blur until the component is re-initialized.
  private _liveValidationEnabled = false;

  enableLiveValidation$ = this.primaryEmailControl.statusChanges.pipe(
    filter(status => status === 'INVALID' && !!this.primaryEmailControl.value),
    take(1),
    tap(() => this._liveValidationEnabled = true),
    takeUntil(this._unsubscribe$),
  );

  private readonly _primaryEmailViewValue$ = new Subject<string>();

  // Commit debounced view values into the control while live validation is on. Before escalation the events
  // are ignored — blur remains the only commit point. emitModelToViewChange:false leaves the input element
  // untouched (the value came from it), so the caret never jumps.
  commitLivePrimaryEmail$ = this._primaryEmailViewValue$.pipe(
    filter(() => this._liveValidationEnabled),
    debounceTime(PRIMARY_EMAIL_LIVE_VALIDATION_DEBOUNCE_TIME),
    distinctUntilChanged(),
    tap(value => {
      this.primaryEmailControl.markAsDirty();
      this.primaryEmailControl.setValue(value, { emitModelToViewChange: false });
    }),
    takeUntil(this._unsubscribe$),
  );

  onPrimaryEmailInput(value: string): void {
    this._primaryEmailViewValue$.next(value);
  }

  constructor(
    private readonly authStateAdaptor: AuthStateAdaptorService,
  ) {
    this.togglePrimaryEmailControlEnabledState$.subscribe();
    this.emitPrimaryEmailChanges$.subscribe();
    this.emitAdditionalEmailsChanges$.subscribe();
    this.enableLiveValidation$.subscribe();
    this.commitLivePrimaryEmail$.subscribe();
  }

  ngOnInit(): void {
    if (this.additionalEmails?.length > 0) {
      const additionalEmailControls = this.additionalEmails
        .map(email => new UntypedFormControl(email, { validators: Validators.email, updateOn: 'blur' }));
      this.additionalEmailsFormArray.controls = additionalEmailControls;
    }

    // Subscribe here, NOT the constructor. These streams emit their seed synchronously on subscribe; in the
    // constructor that first value fires before the host's (isValid)/(primaryEmailPrepopulated) listeners are
    // wired and distinctUntilChanged/take(1) then holds it forever (methods stuck locked). ngOnInit runs after
    // the bindings attach. emitIsValid$ subscribes first to keep the shared source warm for the take(1) below.
    this.emitIsValid$.subscribe();
    this.emitPrimaryEmailPrepopulated$.subscribe();
  }

  private _formatEmail(email: string): string {
    return email.trim().toLowerCase();
  }

  addAdditionalEmailControl(): void {
    if (this.disabled || this.additionalEmailsFormArray.length >= 20) {
      return;
    }

    this.additionalEmailsFormArray.push(new UntypedFormControl('', { validators: Validators.email, updateOn: 'blur' }));
  }

  removeAdditionalEmailControl(index: number): void {
    this.additionalEmailsFormArray.removeAt(index);
  }

  // Focus the primary email input on demand. No-op when the control is disabled (e.g. a signed-in user
  // whose email is fixed) since there is nothing for the payer to correct there.
  focusPrimaryEmail(): void {
    if (this.primaryEmailControl.disabled) {
      return;
    }

    this.primaryEmailInput?.nativeElement.focus();
  }

  validate(): Observable<boolean> {
    this.additionalEmailsFormArray.markAllAsTouched();
    this.additionalEmailsFormArray.updateValueAndValidity();
    if (this.primaryEmailControl.enabled) {
      this.primaryEmailControl.markAsTouched();
      this.primaryEmailControl.updateValueAndValidity();

      // The value reaches state only via a debounced (blur) pipeline; flush it synchronously so a
      // type-then-immediately-Pay click can't submit before state settles.
      if (this.primaryEmailControl.valid) {
        this.primaryEmailChanged.emit(this._formatEmail(this.primaryEmailControl.value));
      }
    }

    // Fun fact: When a control is disabled, both 'control.valid' and 'control.invalid' equal false,
    // so a disabled control must be treated as passing rather than tested for validity.
    return of([this.primaryEmailControl, this.additionalEmailsFormArray].every(control => control.disabled || control.valid));
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
